// pages/TextosLegales.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Statistic,
  Card,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ExportOutlined,
  CopyOutlined,
  GlobalOutlined,
  HomeOutlined,
  MailOutlined,
  FileProtectOutlined
} from '@ant-design/icons';
import axios from 'axios';
import PageContainer from '../components/PageContainer';
import RichTextEditor from '../components/RichTextEditor';
import { BASE_URL } from '../utils/constants';
import './TextosLegales.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TextosLegales = () => {
  // Estados
  const [textos, setTextos] = useState([]);
  const [hoteles, setHoteles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentTexto, setCurrentTexto] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copySourceHotel, setCopySourceHotel] = useState(null);
  const [copyTargetHotel, setCopyTargetHotel] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);

  // Tipos de documento
  const tiposDocumento = [
    { value: 'Cardex', label: 'Cardex', icon: <FileTextOutlined /> },
    { value: 'Cardex Fijo', label: 'Cardex Fijo', icon: <FileTextOutlined /> },
    { value: 'Correo Cliente Final', label: 'Correo Cliente Final', icon: <MailOutlined /> },
    { value: 'Correo Cliente Inicial', label: 'Correo Cliente Inicial', icon: <MailOutlined /> },
    { value: 'Correo Cliente Inicial Asunto', label: 'Correo Cliente Inicial Asunto', icon: <MailOutlined /> },
    { value: 'FormularioDatosCliente', label: 'Formulario Datos Cliente', icon: <FileProtectOutlined /> },
    { value: 'FormularioDatosClientePie', label: 'Formulario Datos Cliente Pie', icon: <FileProtectOutlined /> },
    { value: 'Política Privacidad', label: 'Política Privacidad', icon: <FileProtectOutlined /> },
    { value: 'Términos y Condiciones', label: 'Términos y Condiciones', icon: <FileProtectOutlined /> },
    { value: 'Términos y Condiciones Pie', label: 'Términos y Condiciones Pie', icon: <FileProtectOutlined /> },
    { value: 'Confirmacion Pago', label: 'Confirmación Pago', icon: <FileProtectOutlined /> }
  ];

const idiomas = [  
  { value: 'CAS', label: 'Español', flagCode: 'es' },
  { value: 'CAT', label: 'Catalán', flagCode: 'es-ct' },
  { value: 'ESP', label: 'Español (ESP)', flagCode: 'es' },
  { value: 'FRA', label: 'Francés', flagCode: 'fr' },
  { value: 'ING', label: 'Inglés', flagCode: 'gb' },
  { value: 'GER', label: 'Alemán', flagCode: 'de' },
  { value: 'ITA', label: 'Italiano', flagCode: 'it' }
];


  // Columnas de la tabla
  const columns = [
    {
      title: 'Hotel',
      dataIndex: 'Codigohotel',
      key: 'Codigohotel',
      width: 120,
      sorter: (a, b) => (a.Codigohotel || 0) - (b.Codigohotel || 0),
      sortOrder: sortedInfo.columnKey === 'Codigohotel' && sortedInfo.order,
      render: (codigo) => {
        const hotel = hoteles.find(h => h.codigo === codigo);
        return (
          <Tag color="blue" style={{ fontWeight: 'bold' }}>
            <HomeOutlined style={{ marginRight: 4 }} />
            {hotel ? hotel.nombre : codigo}
          </Tag>
        );
      },
      filters: [],
      filteredValue: filteredInfo.Codigohotel || null,
      onFilter: (value, record) => record.Codigohotel === value,
    },
    {
      title: 'Tipo Documento',
      dataIndex: 'TipoDocumento',
      key: 'TipoDocumento',
      width: 220,
      sorter: (a, b) => (a.TipoDocumento || '').localeCompare(b.TipoDocumento || ''),
      sortOrder: sortedInfo.columnKey === 'TipoDocumento' && sortedInfo.order,
      render: (tipo) => {
        const tipoInfo = tiposDocumento.find(t => t.value === tipo);
        return (
          <Tag color="purple" icon={tipoInfo?.icon || <FileTextOutlined />}>
            {tipoInfo?.label || tipo}
          </Tag>
        );
      },
      filters: tiposDocumento.map(t => ({ text: t.label, value: t.value })),
      filteredValue: filteredInfo.TipoDocumento || null,
      onFilter: (value, record) => record.TipoDocumento === value,
    },
    {
      title: 'Idioma',
      dataIndex: 'Idioma',
      key: 'Idioma',
      width: 100,
      sorter: (a, b) => (a.Idioma || '').localeCompare(b.Idioma || ''),
      sortOrder: sortedInfo.columnKey === 'Idioma' && sortedInfo.order,
      render: (idioma) => {
        const langInfo = idiomas.find(l => l.value === idioma);
        return (
            <Tag color="green">
              <span 
                className={`fi fi-${langInfo?.flagCode}`} 
                style={{ marginRight: 6 }}
              />
              {langInfo?.label || idioma}
            </Tag>
        );
      },
      filters: idiomas.map(l => ({ text: l.label, value: l.value })),
      filteredValue: filteredInfo.Idioma || null,
      onFilter: (value, record) => record.Idioma === value,
    },
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 100,
      sorter: (a, b) => (a.Codigo || '').localeCompare(b.Codigo || ''),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag color="orange" style={{ fontFamily: 'monospace' }}>
          {codigo}
        </Tag>
      ),
    },
    {
      title: 'Contenido',
      dataIndex: 'TextoDocumento',
      key: 'TextoDocumento',
      width: 300,
      ellipsis: true,
      render: (texto) => (
        <Tooltip title={texto ? (texto.length > 100 ? texto.substring(0, 200) + '...' : texto) : 'Sin contenido'}>
          <div style={{ maxWidth: 280 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {texto ? (texto.length > 80 ? texto.substring(0, 80) + '...' : texto) : 'Sin contenido'}
            </Text>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar texto legal?"
            description="Esta acción eliminará el registro permanentemente"
            onConfirm={() => handleDelete(record)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchHoteles();
    fetchTextos();
  }, []);

  // Actualizar filtros de hoteles cuando cambian
  useEffect(() => {
    if (hoteles.length > 0) {
      const hotelFilters = hoteles.map(h => ({ text: h.nombre, value: h.codigo }));
      columns[0].filters = hotelFilters;
    }
  }, [hoteles]);

  const fetchHoteles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/hoteles/listaHoteles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHoteles(response.data.hoteles || []);
    } catch (error) {
      console.error('❌ Error cargando hoteles:', error);
      message.error('Error al cargar los hoteles');
    }
  };

  const fetchTextos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {};
      
      if (searchText) {
        queryParams.search = searchText;
      }
      
      if (filteredInfo.Codigohotel) {
        queryParams.Codigohotel = filteredInfo.Codigohotel[0];
      }
      if (filteredInfo.TipoDocumento) {
        queryParams.TipoDocumento = filteredInfo.TipoDocumento[0];
      }
      if (filteredInfo.Idioma) {
        queryParams.Idioma = filteredInfo.Idioma[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/textoslegales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        TextoDocumento: item.TextoDocumento || ''
      }));
      
      setTextos(cleanedData);
      
    } catch (error) {
      console.error('❌ Error cargando textos legales:', error);
      message.error('Error al cargar los textos legales');
      setTextos([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    fetchTextos();
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentTexto(null);
    form.resetFields();
    form.setFieldsValue({
      Codigo: 'C',
      Idioma: 'CAS'
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentTexto(record);
    form.setFieldsValue({
      Codigohotel: record.Codigohotel,
      TipoDocumento: record.TipoDocumento,
      TextoDocumento: record.TextoDocumento || '',
      Idioma: record.Idioma,
      Codigo: record.Codigo
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    const hotel = hoteles.find(h => h.codigo === record.Codigohotel);
    const tipoInfo = tiposDocumento.find(t => t.value === record.TipoDocumento);
    const langInfo = idiomas.find(l => l.value === record.Idioma);
    
    Modal.info({
      title: `Detalle del Texto Legal`,
      width: 800,
      icon: <FileTextOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <strong>Hotel:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  <HomeOutlined /> {hotel ? hotel.nombre : record.Codigohotel}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Tipo Documento:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {tipoInfo?.icon} {tipoInfo?.label || record.TipoDocumento}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Idioma:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {langInfo?.flag} {langInfo?.label || record.Idioma}
                </Tag>
              </div>
            </Col>
            <Col span={12}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="orange" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {record.Codigo}
                </Tag>
              </div>
            </Col>
            <Col span={24}>
              <Divider />
              <strong>Contenido del documento:</strong>
              <div style={{ 
                marginTop: 8, 
                padding: '16px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {record.TextoDocumento ? (
                  <div dangerouslySetInnerHTML={{ __html: record.TextoDocumento }} />
                ) : (
                  <Text type="secondary">Sin contenido</Text>
                )}
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (record) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/textoslegales/${record.Codigohotel}/${record.TipoDocumento}/${record.Idioma}/${record.Codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTextos(textos.filter(t => 
        !(t.Codigohotel === record.Codigohotel && 
          t.TipoDocumento === record.TipoDocumento && 
          t.Idioma === record.Idioma && 
          t.Codigo === record.Codigo)
      ));
      
      message.success('Texto legal eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando texto legal:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el texto legal');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/textoslegales`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Texto legal creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/textoslegales/${currentTexto.Codigohotel}/${currentTexto.TipoDocumento}/${currentTexto.Idioma}/${currentTexto.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Texto legal actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchTextos();
      
    } catch (error) {
      console.error('❌ Error guardando texto legal:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleOpenCopyModal = () => {
    setCopySourceHotel(null);
    setCopyTargetHotel(null);
    setCopyModalVisible(true);
  };

  const handleCopyTexts = async () => {
    if (!copySourceHotel || !copyTargetHotel) {
      message.warning('Por favor seleccione hotel origen y destino');
      return;
    }

    if (copySourceHotel === copyTargetHotel) {
      message.warning('El hotel origen y destino no pueden ser el mismo');
      return;
    }

    setCopyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/textoslegales/copy`,
        {
          sourceHotel: copySourceHotel,
          targetHotel: copyTargetHotel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(`Se copiaron ${response.data.copiedCount || 0} registros correctamente`);
      setCopyModalVisible(false);
      fetchTextos();
    } catch (error) {
      console.error('❌ Error copiando textos:', error);
      message.error(error.response?.data?.error || 'Error al copiar los textos legales');
    } finally {
      setCopyLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchTextos();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchTextos();
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  

  return (
    <>
      <PageContainer
        title="Textos Legales Check-in Online"
        // subtitle={`${textos.length} documentos registrados`}
        icon={<FileTextOutlined />}
        loading={loading}
        onRefresh={() => fetchTextos()}
        // onCreate={handleCreate}
        // badgeCount={textos.length}
        badgeStatus="processing"
        extra={
          <Space>
            {/* <Button
              icon={<CopyOutlined />}
              onClick={handleOpenCopyModal}
            >
              Copiar textos
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button> */}
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >        
        
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="Buscar por hotel, tipo documento, idioma..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 350 }}
              onPressEnter={handleSearch}
              allowClear
              onClear={handleResetSearch}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={searchLoading}
            >
              Buscar
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetSearch}
            >
              Restablecer
            </Button>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={textos}
          rowKey={(record) => `${record.Codigohotel}-${record.TipoDocumento}-${record.Idioma}-${record.Codigo}`}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200, y: 'calc(100vh - 350px)' }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          pagination={false}
          locale={{
            emptyText: 'No se encontraron textos legales'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar texto legal */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Texto Legal' : 'Editar Texto Legal'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            Codigo: 'C',
            Idioma: 'CAS'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Codigohotel"
                label="Hotel"
                rules={[{ required: true, message: 'Por favor seleccione un hotel' }]}
              >
                <Select
                  placeholder="Seleccione un hotel"
                  showSearch
                  optionFilterProp="children"
                  disabled={modalType === 'edit'}
                >
                  {hoteles.map(hotel => (
                    <Option key={hotel.codigo} value={hotel.codigo}>
                      <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="TipoDocumento"
                label="Tipo de Documento"
                rules={[{ required: true, message: 'Por favor seleccione un tipo de documento' }]}
              >
                <Select
                  placeholder="Seleccione un tipo"
                  showSearch
                  optionFilterProp="children"
                  disabled={modalType === 'edit'}
                >
                  {tiposDocumento.map(tipo => (
                    <Option key={tipo.value} value={tipo.value}>
                      {tipo.icon} {tipo.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Idioma"
                label="Idioma"
                rules={[{ required: true, message: 'Por favor seleccione un idioma' }]}
              >
                <Select
                  placeholder="Seleccione un idioma"
                  disabled={modalType === 'edit'}
                >
                  {idiomas.map(idioma => (
                    <Option key={idioma.value} value={idioma.value}>
                      {idioma.flag} {idioma.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[
                  { required: true, message: 'Por favor ingrese el código' },
                  { max: 10, message: 'Máximo 10 caracteres' }
                ]}
                tooltip="Código identificador del documento (ej: C, CCF, CCI, PP, TC)"
              >
                <Input 
                  placeholder="Ej: C, CCF, CCI, PP, TC"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="TextoDocumento"
            label="Contenido del Documento"
            tooltip="Puede usar HTML para dar formato al texto"
          >
            <TextArea
              rows={15}
              placeholder="Escriba el contenido del documento aquí... Puede usar etiquetas HTML para formato"
              showCount
              maxLength={50000}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
            {/* <Form.Item
                name="TextoDocumento"
                label="Contenido del Documento"
                tooltip="Editor estilo Word - el formato se guarda automáticamente como HTML"
            >
                <RichTextEditor 
                    placeholder="Escribe aquí el contenido del documento..."
                />
            </Form.Item>           */}

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Códigos comunes:</strong> C (Cardex), CCF (Correo Cliente Final), CCI (Correo Cliente Inicial), CCIA (Asunto), PP (Política Privacidad), TC (Términos y Condiciones)</li>
                  <li><strong>Variables disponibles:</strong> %nombre%, %hotel%, %localizador%, %fechaentrada%, %fechasalida%, %url%, etc.</li>
                  <li><strong>Formato HTML:</strong> Puede usar etiquetas HTML para dar formato al texto</li>
                  <li><strong>Idiomas soportados:</strong> CAS, CAT, ESP, FRA, ING, GER, ITA</li>
                </ul>
              </div>
            </Card>
          )}
        </Form>
      </Modal>

      {/* Modal para copiar textos entre hoteles */}
      <Modal
        title="Copiar Textos Legales entre Hoteles"
        open={copyModalVisible}
        onOk={handleCopyTexts}
        onCancel={() => setCopyModalVisible(false)}
        width={500}
        okText="Copiar"
        cancelText="Cancelar"
        confirmLoading={copyLoading}
      >
        <Alert
          message="Información"
          description="Esta acción copiará TODOS los textos legales del hotel origen al hotel destino. Los textos existentes en el hotel destino serán reemplazados."
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />
        
        <Form layout="vertical">
          <Form.Item label="Hotel Origen" required>
            <Select
              placeholder="Seleccione el hotel de origen"
              showSearch
              optionFilterProp="children"
              value={copySourceHotel}
              onChange={setCopySourceHotel}
            >
              {hoteles.map(hotel => (
                <Option key={hotel.codigo} value={hotel.codigo}>
                  <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Hotel Destino" required>
            <Select
              placeholder="Seleccione el hotel de destino"
              showSearch
              optionFilterProp="children"
              value={copyTargetHotel}
              onChange={setCopyTargetHotel}
            >
              {hoteles.map(hotel => (
                <Option key={hotel.codigo} value={hotel.codigo}>
                  <HomeOutlined /> {hotel.codigo} - {hotel.nombre}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TextosLegales;